import { NavigationBack } from "@/components";
import MediaThumbnail from "@/components/video/Thumbnail";
import useGallery from "@/hooks/useGallery";
import useProfile from "@/hooks/useProfile";
import { ASPECT_RATIO } from "@/libraries/constant";
import { randFileName } from "@/libraries/helper";
import { CameraSearchParamSchema, MESSAGE_TAG } from "@/schemas";
import { fileService } from "@/services";
import { useGalleryStore } from "@/stores";
import FlipCameraAndroidRoundedIcon from "@mui/icons-material/FlipCameraAndroidRounded";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Link, createFileRoute, useRouterState } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import orderBy from "lodash/orderBy";
import upperFirst from "lodash/upperFirst";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import { toast } from "sonner";
import PhotoAction from "./-components/PhotoAction";
import VideoAction from "./-components/VideoAction";

export const Route = createFileRoute("/dashboard/camera/")({
	validateSearch: zodSearchValidator(CameraSearchParamSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const queryAction = Route.useSearch({
		select: (s: CameraSearchParamSchema) => s.action,
	});
	const messageTag = Route.useSearch({
		select: (state) => state.tag,
	});
	const channelName = useRouterState({
		// @ts-expect-error
		select: (s) => s.location.state?.channelName ?? null,
	});

	const webcamRef = useRef<Webcam>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const [capturing, setCapturing] = useState(false);
	const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
	const [aspectRatio, setAspectRatio] =
		useState<keyof typeof ASPECT_RATIO>("3:4");
	const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

	const { profile } = useProfile();
	const { onCaptureImage } = useGallery();

	const addImages = useGalleryStore((state) => state.addImages);

	const currentFileStorage = useMemo(() => {
		if (!profile?.storages) return null;
		const sortedStorages = orderBy(
			profile.storages,
			[(item) => new Date(item.created_at)],
			["desc"],
		);
		return sortedStorages[0];
	}, [profile?.storages]);

	const videoConstraints = useMemo<MediaTrackConstraints>(
		() => ({
			width: 1280,
			height: 720,
			facingMode,
			// deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
			aspectRatio: ASPECT_RATIO[aspectRatio],
		}),
		[facingMode, aspectRatio],
	);

	const isEnableToChangeAction = useMemo(
		() => messageTag !== MESSAGE_TAG.MEDIA,
		[messageTag],
	);

	const handleSwitchDevice = useCallback(
		(e?: React.MouseEvent<HTMLButtonElement>) => {
			e?.preventDefault();
			return setFacingMode(facingMode === "user" ? "environment" : "user");
		},
		[facingMode],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const handleOnCaptureImage = useCallback(async () => {
		if (!webcamRef.current) return;
		const imageSrc = webcamRef.current.getScreenshot();
		if (!imageSrc) return;
		await onCaptureImage(imageSrc);
		return navigate({
			replace: !!messageTag,
			to: "/dashboard/gallery/view",
			search: {
				tag: messageTag,
			},
			state: {
				channelName,
			},
		});
	}, [webcamRef, messageTag, channelName, navigate, onCaptureImage]);

	const handleDataAvailable = useCallback(({ data }: BlobEvent) => {
		if (data.size > 0) {
			setRecordedChunks((prev) => [...prev, data]);
		}
	}, []);

	const handleOnCaptureVideo = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			if (!capturing) {
				setRecordedChunks([]);
				if (webcamRef.current?.stream) {
					const stream = webcamRef.current.stream;
					const mediaRecorder = new MediaRecorder(stream, {
						mimeType: "video/webm",
					});

					mediaRecorderRef.current = mediaRecorder;
					mediaRecorder.addEventListener("dataavailable", handleDataAvailable);
					mediaRecorder.start();
					return setCapturing(true);
				}
			}
			if (mediaRecorderRef.current) {
				mediaRecorderRef.current.stop();
			}
			return setCapturing(false);
		},
		[capturing, handleDataAvailable],
	);

	const handleOnRetieveVideoChunks = useCallback(async () => {
		if (capturing || !recordedChunks.length) return;
		const blob = new Blob(recordedChunks, { type: "video/webm" });
		const file = await fileService.blobToFile(blob, randFileName(".webm"));
		addImages(file);
		setRecordedChunks([]);
		return navigate({
			replace: !!messageTag,
			to: "/dashboard/gallery/view",
			search: {
				tag: messageTag,
			},
			state: {
				channelName,
			},
		});
	}, [recordedChunks, capturing, addImages, messageTag, channelName, navigate]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!capturing && recordedChunks.length) handleOnRetieveVideoChunks();
	}, [capturing, recordedChunks]);

	return (
		<div className="relative bg-dark h-full w-full overflow-hidden">
			<div className="absolute z-10 top-0 left-0 w-full h-full">
				<div className="w-full h-full flex flex-col justify-between">
					<div className="container h-16 flex flex-col justify-center items-start">
						<NavigationBack />
					</div>
					<div className="flex flex-col">
						{queryAction === "photo" ? (
							<div className="py-4">
								<div className="relative mx-auto w-fit px-4 py-2 rounded-full bg-dark/65">
									<ToggleButtonGroup
										color="primary"
										value={aspectRatio}
										size="small"
										exclusive
										onChange={(_, val) => val && setAspectRatio(val)}
										aria-label="Aspect Ratio"
										className="gap-2 !rounded-full [&_button.Mui-selected]:!border-none [&_button.Mui-selected]:!bg-primary [&_button.Mui-selected]:!text-white"
									>
										{Object.keys(ASPECT_RATIO).map((aspect) => (
											<ToggleButton
												key={aspect}
												value={aspect}
												aria-label={aspect}
												className="!bg-dark/40 !rounded-full !px-4 !py-2 !text-white"
											>
												{aspect}
											</ToggleButton>
										))}
									</ToggleButtonGroup>
								</div>
							</div>
						) : null}
						<div className="w-full flex flex-col gap-2 py-4 bg-dark/65 justify-center items-center">
							{isEnableToChangeAction ? (
								<ToggleButtonGroup
									color="primary"
									value={queryAction}
									size="small"
									exclusive
									onChange={(_, val) =>
										val &&
										navigate({
											replace: true,
											search: (oldSearch: CameraSearchParamSchema) => ({
												...oldSearch,
												action: val,
											}),
											state: {
												channelName,
											},
										})
									}
									aria-label="Aspect Ratio"
									className="gap-4 !rounded-full !border-transparent [&_button.Mui-selected]:!text-white"
									disabled={queryAction === "video" && capturing}
								>
									{["photo", "video"].map((action) => (
										<ToggleButton
											key={action}
											value={action}
											aria-label={action}
											className="!text-white !border-none !bg-transparent"
										>
											{upperFirst(action)}
										</ToggleButton>
									))}
								</ToggleButtonGroup>
							) : null}
							<div className="w-full flex flex-row justify-between items-center px-4">
								<Link
									to="/dashboard/gallery"
									className="flex-none relative w-14 h-14 bg-white outline outline-brand"
									disabled={capturing}
								>
									{currentFileStorage ? (
										<MediaThumbnail
											src={currentFileStorage.file_id}
											className="h-full"
										/>
									) : null}
								</Link>
								{queryAction === "photo" ? (
									<PhotoAction title="Capture" onClick={handleOnCaptureImage} />
								) : (
									<VideoAction
										state={capturing ? "stop" : "start"}
										onClick={handleOnCaptureVideo}
									/>
								)}
								<IconButton
									color="primary"
									size="large"
									title="Switch Camera"
									className="!border-2 !border-white !bg-white/20"
									onClick={handleSwitchDevice}
								>
									<FlipCameraAndroidRoundedIcon className="!text-white" />
								</IconButton>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="relative h-full">
				<Webcam
					ref={webcamRef}
					muted={true}
					screenshotFormat="image/jpeg"
					audio={queryAction === "video"}
					mirrored={videoConstraints.facingMode === "user"}
					videoConstraints={videoConstraints}
					className="w-full h-full object-contain"
					style={{ aspectRatio: aspectRatio.replace(":", "/") }}
					onUserMediaError={(err) => {
						if (
							err instanceof DOMException &&
							err.name === "NotReadableError"
						) {
							handleSwitchDevice();
						} else {
							toast.error("Camera error: Failed to access camera");
						}
					}}
				/>
			</div>
		</div>
	);
}
